import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import { LumynBadge } from '../components/common/LumynBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { getInitials } from '../utils/helpers';
import { User, Mail, Shield, Building2, Briefcase, Phone, Save, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

export default function ProfilePage() {
  const { user, refreshSession } = useAuth();
  const { put } = useApi();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    display_name: user?.display_name || '',
    phone: user?.phone || '',
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await put('/users/me', form);
      toast.success('Profile updated!');
      setEditing(false);
      refreshSession();
    } catch { toast.error('Failed to update profile'); }
    finally { setSaving(false); }
  };

  if (!user) return null;

  return (
    <div className="max-w-2xl mx-auto space-y-5 relative" data-testid="profile-page">
      <div aria-hidden className="page-blob bg-[var(--lumyn-brand)]" />
      <div className="relative z-10 space-y-5">
        <div className="space-y-1.5">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[var(--lumyn-brand)]/10 text-[var(--lumyn-brand)] text-[11px] font-semibold uppercase tracking-[0.14em]">
            <User className="w-3.5 h-3.5" />{'Profile'}
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight">{'Your profile'}</h1>
        </div>

      {/* Avatar & Info — LinkedIn-style banner card */}
      <Card className="overflow-hidden">
        <div aria-hidden className="h-28 bg-panel relative">
        </div>
        <CardContent className="pt-0 pb-6">
          <div className="flex items-end gap-5 -mt-10">
            <div className="w-20 h-20 rounded-full bg-gold-100 text-gold-800 flex items-center justify-center text-2xl font-display font-semibold shrink-0 ring-1 ring-gold/40" data-testid="profile-avatar">
              {getInitials(`${user.first_name} ${user.last_name}`)}
            </div>
            <div className="space-y-1 pb-1">
              <h2 className="font-display text-xl font-semibold tracking-tight">{user.first_name} {user.last_name}</h2>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Mail className="w-3.5 h-3.5" />{user.email}
              </div>
              <div className="flex items-center gap-2 pt-0.5">
                <LumynBadge variant={user.role} type="role" size="sm" />
                {!user.has_password && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-medium"><KeyRound className="w-3 h-3" /> OTP Only</span>}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Details */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-display text-lg tracking-tight">{'Personal Information'}</CardTitle>
          {!editing && <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="edit-btn">{'Edit'}</Button>}
        </CardHeader>
        <CardContent className="space-y-4">
          {editing ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-xs text-muted-foreground">{'First Name'}</label><Input value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} data-testid="input-first-name" /></div>
                <div><label className="text-xs text-muted-foreground">{'Last Name'}</label><Input value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} data-testid="input-last-name" /></div>
              </div>
              <div><label className="text-xs text-muted-foreground">{'Display Name'}</label><Input value={form.display_name} onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))} data-testid="input-display-name" /></div>
              <div><label className="text-xs text-muted-foreground">{'Phone'}</label><Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} data-testid="input-phone" /></div>
              <div className="flex gap-3 pt-2">
                <Button onClick={handleSave} disabled={saving} data-testid="save-btn"><Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Changes'}</Button>
                <Button variant="outline" onClick={() => setEditing(false)}>{'Cancel'}</Button>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: User, label: 'First Name', value: user.first_name },
                { icon: User, label: 'Last Name', value: user.last_name },
                { icon: Mail, label: 'Email', value: user.email },
                { icon: Shield, label: 'Role', value: user.role },
                { icon: Building2, label: 'Department', value: user.department || '—' },
                { icon: Briefcase, label: 'Job Title', value: user.job_title || '—' },
                { icon: Phone, label: 'Phone', value: user.phone || '—' },
              ].map(item => (
                <div key={item.label} className="space-y-1">
                  <p className="text-xs text-muted-foreground flex items-center gap-1"><item.icon className="w-3 h-3" />{item.label}</p>
                  <p className="text-sm font-medium">{item.value}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      </div>
    </div>
  );
}
